import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { FixedSizeList as List } from 'react-window';
import { useNavigate, useLocation, useOutletContext, useSearchParams } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Edit, FileText, Calendar } from "lucide-react";
import { toast } from "react-toastify";
import axios from "axios";
import UserDayView from './UserDayView';
import AddOrEditUserForm from './../company/UserCreator';
import EditQualificationDialog from './EditQualificationDialog';
import TrainingEditModal from './TrainingEditModal';
import TrainingMailSender from './TrainingMailSender';
import * as Tooltip from '@radix-ui/react-tooltip';

// Import extracted components
import { getWeekStartDate } from './MainboardPanel/components/helpers';
import CalendarCell from './MainboardPanel/components/CalendarCell';
import TrainingsTable from './MainboardPanel/components/TrainingsTable';
import BulkEditDialog from './MainboardPanel/components/BulkEditDialog';
import FiltersPanel from './MainboardPanel/components/FiltersPanel';
import HeaderToolbar from './MainboardPanel/components/HeaderToolbar';

const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, "") || ""}/api`;

/**
 * 🔍 DEBUGGING LOGS FOR OPTIMIZED TRAINING DATA FETCHING
 * 
 * Look for these console log prefixes to track data fetching:
 * 🔍 - Analysis phase (deciding what to fetch)
 * 🚀 - First load/forced reload 
 * 📅 - Backward scroll (fetching older data)
 * ✅ - Data already available (skipping fetch)
 * 🌐 - API request being made
 * ⏩ - API request skipped (using cache)
 * 📦 - Processing API response
 * 🔄 - Data replacement
 * 📋 - Data merging
 * 📚 - Using existing data
 * ⬅️ - Calendar extending backward
 * ➡️ - Calendar extending forward  
 * 👤 - User actions triggering reload
 */

// MainboardPanel Component
const MainboardPanel = ({ view = "trainings" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // New scrollable calendar state - 2 days back to 60 days forward
  // Or start from week number if provided in URL
  const [scrollableCalendarStart, setScrollableCalendarStart] = useState(() => {
    const weekParam = searchParams.get('week');
    if (weekParam) {
      const weekNumber = parseInt(weekParam, 10);
      const currentYear = new Date().getFullYear();
      const weekStart = getWeekStartDate(currentYear, weekNumber);
      weekStart.setDate(weekStart.getDate() - 2);
      return weekStart;
    }
    const today = new Date();
    today.setDate(today.getDate() - 2);
    return today;
  });
  const [scrollableCalendarEnd, setScrollableCalendarEnd] = useState(() => {
    const weekParam = searchParams.get('week');
    if (weekParam) {
      const weekNumber = parseInt(weekParam, 10);
      const currentYear = new Date().getFullYear();
      const weekStart = getWeekStartDate(currentYear, weekNumber);
      const endDate = new Date(weekStart);
      endDate.setDate(weekStart.getDate() + 60);
      return endDate;
    }
    const today = new Date();
    today.setDate(today.getDate() + 60);
    return today;
  });
  
  const [zoomLevel, setZoomLevel] = useState(1.0);
  const [loading, setLoading] = useState(true);
  
  // Refs for scrollable calendar
  const scrollContainerRef = useRef(null);
  const scrollableParentRef = useRef(null);
  const listRef = useRef(null);
  const scrollLeftRef = useRef(0);

  // Virtualization state
  const [scrollTop, setScrollTop] = useState(0);
  const ROW_HEIGHT = 50;
  const OVERSCAN = 5;
  const [users, setUsers] = useState([]);
  const [userWork, setUserWork] = useState([]);
  const [userEvents, setUserEvents] = useState([]);
  const [userExpirations, setUserExpirations] = useState([]);
  const [trainingOptions, setTrainingOptions] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [loadedTrainingsDateRange, setLoadedTrainingsDateRange] = useState({ start: null, end: null });
  const [userQualifications, setUserQualifications] = useState([]);
  const [qualificationTypes, setQualificationTypes] = useState([]);
  const [selectedUserDay, setSelectedUserDay] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Active filters from FiltersPanel
  const [activeFilters, setActiveFilters] = useState({
    searchTerm: '',
    selectedRoles: [],
    selectedSites: [],
    selectedCountries: [],
    selectedManagers: [],
    selectedSupervisors: [],
    selectedCoordinators: [],
    selectedQualifications: [],
    selectedTrainingCenters: [],
    selectedCreatedBy: [],
    selectedStatuses: [],
    trainingDateRange: { start: '', end: '' },
    showCertificateIssues: false
  });
  
  const [editingTraining, setEditingTraining] = useState(null);
  const [editingMultiple, setEditingMultiple] = useState(null);
  const [editingQualification, setEditingQualification] = useState(null);
  const [mailSenderData, setMailSenderData] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [userQualificationStatus, setUserQualificationStatus] = useState({});
  const companyId = 1;
  const { sidebarCollapsed } = useOutletContext();
  const [isUserFormOpen, setIsUserFormOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [selectedTrainings, setSelectedTrainings] = useState([]);
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [viewType, setViewType] = useState(view === 'users' ? 'users' : 'trainings');
  const [displayMode, setDisplayMode] = useState(view === 'users' ? 'trainings' : 'table');
  
  // Update view when prop changes
  useEffect(() => {
    if (view === 'users') {
      setViewType('users');
      setDisplayMode('trainings');
    } else {
      setViewType('trainings');
      setDisplayMode('table');
    }
  }, [view]);
  
  // Date range picker state
  const [isDateRangePickerOpen, setIsDateRangePickerOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [isCustomDateRange, setIsCustomDateRange] = useState(false);

  // File drag and drop states
  const [draggedFiles, setDraggedFiles] = useState([]);
  const [draggedOverUser, setDraggedOverUser] = useState(null);
  const [isFileQualificationModalOpen, setIsFileQualificationModalOpen] = useState(false);
  const [selectedUserForFiles, setSelectedUserForFiles] = useState(null);

  // Calendar helper functions - memoized for performance
  const daysForView = useMemo(() => {
    const days = [];

    if (isCustomDateRange && customStartDate && customEndDate) {
      const startDate = new Date(customStartDate);
      const endDate = new Date(customEndDate);
      const diffTime = Math.abs(endDate - startDate);
      const daysToShow = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

      for (let i = 0; i < daysToShow; i++) {
        const day = new Date(startDate);
        day.setDate(startDate.getDate() + i);
        days.push(day);
      }
    } else {
      const currentDay = new Date(scrollableCalendarStart);
      while (currentDay <= scrollableCalendarEnd) {
        days.push(new Date(currentDay));
        currentDay.setDate(currentDay.getDate() + 1);
      }
    }

    return days;
  }, [isCustomDateRange, customStartDate, customEndDate, scrollableCalendarStart, scrollableCalendarEnd]);

  const columnWidth = useMemo(() => {
    const userColumnWidth = 250;
    const margin = 18;
    const sidebarWidth = sidebarCollapsed ? 64 : 256;
    const availableWidth = windowWidth - userColumnWidth - margin - sidebarWidth;

    const biweekDaysCount = 14;
    const calculatedWidth = availableWidth / biweekDaysCount;
    const minWidth = 70;

    return `${Math.max(calculatedWidth, minWidth)}px`;
  }, [windowWidth, sidebarCollapsed]);

  const gridMinWidth = useMemo(() => {
    const colWidth = parseFloat(columnWidth);
    const totalWidth = 250 + colWidth * daysForView.length;
    return `${totalWidth}px`;
  }, [columnWidth, daysForView.length]);

  // Memoized grid style to prevent re-renders
  const gridStyle = useMemo(() => ({
    gridTemplateColumns: `250px repeat(${daysForView.length}, ${columnWidth})`,
    transform: `scale(${zoomLevel})`,
    transformOrigin: "top left",
    width: `${100 / zoomLevel}%`,
  }), [daysForView.length, columnWidth, zoomLevel]);

  const handleUserSave = useCallback(() => {
    console.log('👤 [MainboardPanel] User saved - forcing full data reload');
    setIsUserFormOpen(false);
    setEditingUserId(null);
    fetchData(true);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragEnter = useCallback((e, userId) => {
    e.preventDefault();
    e.stopPropagation();
    setDraggedOverUser(userId);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDraggedOverUser(null);
    }
  }, []);

  const handleFileDrop = useCallback((e, userId) => {
    e.preventDefault();
    e.stopPropagation();
    setDraggedOverUser(null);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    const validExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'];
    const validFiles = files.filter(file => {
      const extension = '.' + file.name.split('.').pop().toLowerCase();
      return validExtensions.includes(extension);
    });

    if (validFiles.length === 0) {
      toast.error("Please drop valid certificate files (PDF, JPG, PNG, DOC, DOCX)");
      return;
    }

    if (validFiles.length !== files.length) {
      toast.warn(`${files.length - validFiles.length} files were skipped (unsupported format)`);
    }

    const user = users.find(u => u.id === userId);
    if (!user) {
      toast.error("User not found");
      return;
    }

    setDraggedFiles(validFiles);
    setSelectedUserForFiles(user);
    setIsFileQualificationModalOpen(true);
  }, [users]);

  const handleCloseFileQualificationModal = useCallback(() => {
    setIsFileQualificationModalOpen(false);
    setDraggedFiles([]);
    setSelectedUserForFiles(null);
  }, []);

  const handleDateRangeApply = useCallback(() => {
    if (customStartDate && customEndDate) {
      const startDate = new Date(customStartDate);
      const endDate = new Date(customEndDate);

      if (startDate > endDate) {
        toast.error("Start date cannot be after end date");
        return;
      }

      const diffTime = Math.abs(endDate - startDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

      setScrollableCalendarStart(startDate);
      setScrollableCalendarEnd(endDate);
      setLoadedTrainingsDateRange({ start: null, end: null });
      setIsCustomDateRange(true);

      setIsDateRangePickerOpen(false);
      setCustomStartDate("");
      setCustomEndDate("");
      toast.success(`Date range set: ${diffDays} days`);
    } else {
      toast.error("Please select both start and end dates");
    }
  }, [customStartDate, customEndDate]);

  const handleDateRangeReset = useCallback(() => {
    setIsCustomDateRange(false);
    setCustomStartDate("");
    setCustomEndDate("");

    const today = new Date();
    const start = new Date(today);
    start.setDate(today.getDate() - 2);
    const end = new Date(today);
    end.setDate(today.getDate() + 60);
    setScrollableCalendarStart(start);
    setScrollableCalendarEnd(end);

    // Reset loaded trainings range to force reload
    setLoadedTrainingsDateRange({ start: null, end: null });

    setIsDateRangePickerOpen(false);
    toast.success("Reset to default calendar view");
  }, []);

  const handleFiltersChange = useCallback((filters) => {
    setActiveFilters(filters);
  }, []);

  // Navigation functions
  const handleNavigateBack = useCallback(() => {
    const newStart = new Date(scrollableCalendarStart);
    const newEnd = new Date(scrollableCalendarEnd);
    newStart.setDate(newStart.getDate() - 30);
    newEnd.setDate(newEnd.getDate() - 30);
    setScrollableCalendarStart(newStart);
    setScrollableCalendarEnd(newEnd);
    setLoadedTrainingsDateRange({ start: null, end: null });
  }, [scrollableCalendarStart, scrollableCalendarEnd]);

  const handleNavigateForward = useCallback(() => {
    const newStart = new Date(scrollableCalendarStart);
    const newEnd = new Date(scrollableCalendarEnd);
    newStart.setDate(newStart.getDate() + 30);
    newEnd.setDate(newEnd.getDate() + 30);
    setScrollableCalendarStart(newStart);
    setScrollableCalendarEnd(newEnd);
    setLoadedTrainingsDateRange({ start: null, end: null });
  }, [scrollableCalendarStart, scrollableCalendarEnd]);

  const handleNavigateToday = useCallback(() => {
    const today = new Date();
    const start = new Date(today);
    start.setDate(today.getDate() - 2);
    const end = new Date(today);
    end.setDate(today.getDate() + 60);
    setScrollableCalendarStart(start);
    setScrollableCalendarEnd(end);
    setIsCustomDateRange(false);
    setLoadedTrainingsDateRange({ start: null, end: null });
  }, []);




  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch((err) => {
        console.error("Error entering fullscreen:", err);
        toast.error("Failed to enter fullscreen mode");
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      }).catch((err) => {
        console.error("Error exiting fullscreen:", err);
        toast.error("Failed to exit fullscreen mode");
      });
    }
  };



  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  const getRoleTooltip = (user) => {
    const roleQualifications = user.training_role_data?.qualificationmatrix_data || [];

    if (!user.training_role_data) {
      return "No role assigned";
    }

    if (roleQualifications.length === 0) {
      return `<div class="text-slate-300">Role: ${user.training_role_data.name}<br/>No qualifications required for this role</div>`;
    }

    return `
      <div>
        <p class="text-orange-300 font-medium mb-2">Role: ${user.training_role_data.name}</p>
        <p class="text-xs text-slate-400 mb-1">Required qualifications for this role:</p>
        <ul class="text-xs text-slate-300 list-disc pl-4">
          ${roleQualifications.map(qual =>
            `<li>${qual.qualification_type.name}</li>`
          ).join('')}
        </ul>
      </div>
    `;
  };

  const getUserQualificationStatus = (userId, qualifications, trainingEvents, user) => {
    const roleQualifications = user.training_role_data?.qualificationmatrix_data || [];

    if (!roleQualifications.length) {
      return {
        color: "bg-steel-50",
        hoverColor: "bg-steel-100",
        tooltip: "No assigned role or role has no defined qualifications"
      };
    }

    const allUserQualifications = qualifications.filter(q => q.user === userId);
    const userQualifications = allUserQualifications.filter(userQual => 
      roleQualifications.some(roleQual => 
        roleQual.qualification_type.id === userQual.qualification_type_data?.id
      )
    );
    
    const nonRoleQualifications = allUserQualifications.filter(userQual => 
      !roleQualifications.some(roleQual => 
        roleQual.qualification_type.id === userQual.qualification_type_data?.id
      )
    );

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const missingQualifications = roleQualifications.filter(roleQual =>
      !allUserQualifications.some(userQual =>
        userQual.qualification_type_data?.id === roleQual.qualification_type.id
      )
    ).map(roleQual => {
      const plannedTraining = trainingEvents.find(event =>
        event.userId === userId &&
        event.qualificationId === roleQual.qualification_type.id &&
        new Date(event.start_date) > today &&
        (event.status === 'planned' || event.status === 'approved')
      );
      
      return {
        ...roleQual,
        plannedTraining
      };
    });

    const missingWithoutTraining = missingQualifications.filter(qual => !qual.plannedTraining);
    const missingWithTraining = missingQualifications.filter(qual => qual.plannedTraining);

    let worstStatus = "green";
    const expiredQualifications = [];
    const orangeQualifications = [];
    const yellowQualifications = [];
    const plannedTrainings = new Map();

    for (const qualification of userQualifications) {
      if (qualification.expiry_date) {
        const expiryDate = new Date(qualification.expiry_date);
        expiryDate.setHours(0, 0, 0, 0);
        const daysRemaining = Math.floor((expiryDate - today) / (1000 * 60 * 60 * 24));

        const relatedTraining = trainingEvents.find(event => 
          event.userId === userId &&
          event.qualificationId === qualification.qualification_type_data?.id &&
          new Date(event.start_date) > today &&
          (event.status === 'planned' || event.status === 'approved')
        );

        if (relatedTraining) {
          plannedTrainings.set(qualification.id, relatedTraining);
        }

        if (daysRemaining <= 0) {
          expiredQualifications.push({ ...qualification, daysRemaining, relatedTraining });
          if (!relatedTraining) {
            worstStatus = "red";
          }
        } else if (daysRemaining <= 30) {
          orangeQualifications.push({ ...qualification, daysRemaining, relatedTraining });
          if (!relatedTraining && worstStatus !== "red") {
            worstStatus = "orange";
          }
        } else if (daysRemaining <= 60) {
          yellowQualifications.push({ ...qualification, daysRemaining, relatedTraining });
          if (!relatedTraining && worstStatus !== "red" && worstStatus !== "orange") {
            worstStatus = "yellow";
          }
        }
      }
    }

    if (missingWithoutTraining.length > 0) {
      worstStatus = "red";
    }

    const sortByExpiryDate = (a, b) => new Date(a.expiry_date) - new Date(b.expiry_date);
    expiredQualifications.sort(sortByExpiryDate);
    orangeQualifications.sort(sortByExpiryDate);
    yellowQualifications.sort(sortByExpiryDate);
    
    const expiringNonRoleQualifications = [];
    for (const qualification of nonRoleQualifications) {
      if (qualification.expiry_date) {
        const expiryDate = new Date(qualification.expiry_date);
        expiryDate.setHours(0, 0, 0, 0);
        const daysRemaining = Math.floor((expiryDate - today) / (1000 * 60 * 60 * 24));
        
        if (daysRemaining <= 60) {
          expiringNonRoleQualifications.push({ ...qualification, daysRemaining });
        }
      }
    }
    expiringNonRoleQualifications.sort(sortByExpiryDate);

    let color = "bg-green-200";
    let hoverColor = "bg-green-300";
    
    if (worstStatus === "red") {
      color = "bg-red-200";
      hoverColor = "bg-red-300";
    } else if (worstStatus === "orange") {
      color = "bg-orange-100";
      hoverColor = "bg-orange-200";
    } else if (worstStatus === "yellow") {
      color = "bg-yellow-100";
      hoverColor = "bg-yellow-200";
    }

    let tooltip = `
      <div class="mb-2 pb-2 border-b border-slate-600">
        <p class="font-medium">${user.first_name} ${user.last_name}</p>
        <p class="text-slate-300">@${user.username}</p>
      </div>
    `;

    if (missingWithoutTraining.length > 0) {
      tooltip += `
        <p class="font-medium mb-1 text-red-300">🔴 Missing Required Certificates:</p>
        <ul class="list-disc pl-4 mb-3">
          ${missingWithoutTraining.map(qual => 
            `<li class="text-red-200">${qual.qualification_type.name}</li>`
          ).join('')}
        </ul>
      `;
    }

    if (missingWithTraining.length > 0) {
      tooltip += `
        <p class="font-medium mb-1 text-orange-300">📅 Missing Certificates with Planned Training:</p>
        <ul class="list-disc pl-4 mb-3">
          ${missingWithTraining.map(qual =>
            `<li class="text-orange-200">${qual.qualification_type.name} (training planned on ${new Date(qual.plannedTraining.start_date).toLocaleDateString()})</li>`
          ).join('')}
        </ul>
      `;
    }

    if (expiredQualifications.length > 0) {
      tooltip += `
        <p class="font-medium mb-1 text-red-300">⚠️ Expired Certificates:</p>
        <ul class="list-disc pl-4 mb-3">
          ${expiredQualifications.map(q => 
            `<li class="text-red-200">Certificate "${q.qualification_type_data?.name || 'No data'}" expired on ${new Date(q.expiry_date).toLocaleDateString()}${q.relatedTraining ? ` (training planned on ${new Date(q.relatedTraining.start_date).toLocaleDateString()})` : ''}</li>`
          ).join("")}
        </ul>
      `;
    }

    if (orangeQualifications.length > 0) {
      tooltip += `
        <p class="font-medium mb-1 text-orange-300">🟠 Certificates Expiring Within 30 Days:</p>
        <ul class="list-disc pl-4 mb-3">
          ${orangeQualifications.map(q => 
            `<li class="text-orange-200">Certificate "${q.qualification_type_data?.name || 'No data'}" expires in ${q.daysRemaining} days (${new Date(q.expiry_date).toLocaleDateString()})${q.relatedTraining ? ` (training planned on ${new Date(q.relatedTraining.start_date).toLocaleDateString()})` : ''}</li>`
          ).join("")}
        </ul>
      `;
    }

    if (yellowQualifications.length > 0) {
      tooltip += `
        <p class="font-medium mb-1 text-yellow-300">🟡 Certificates Expiring Within 60 Days:</p>
        <ul class="list-disc pl-4 mb-3">
          ${yellowQualifications.map(q => 
            `<li class="text-yellow-200">Certificate "${q.qualification_type_data?.name || 'No data'}" expires in ${q.daysRemaining} days (${new Date(q.expiry_date).toLocaleDateString()})${q.relatedTraining ? ` (training planned on ${new Date(q.relatedTraining.start_date).toLocaleDateString()})` : ''}</li>`
          ).join("")}
        </ul>
      `;
    }

    if (missingWithoutTraining.length === 0 && missingWithTraining.length === 0 && expiredQualifications.length === 0 && orangeQualifications.length === 0 && yellowQualifications.length === 0) {
      tooltip += `<p class="text-green-300">✅ All required qualifications are valid for more than 60 days</p>`;
    }

    if (expiringNonRoleQualifications.length > 0) {
      tooltip += `
        <div class="mt-3 pt-2 border-t border-slate-600">
          <p class="text-xs text-slate-400 mb-1">Expiring certificates not required for the user's role:</p>
          <ul class="text-xs text-slate-300 list-disc pl-4">
            ${expiringNonRoleQualifications.map(q => {
              const statusText = q.daysRemaining <= 0 ? 'expired' : `expires in ${q.daysRemaining} days`;
              const colorClass = q.daysRemaining <= 0 ? 'text-red-300' : q.daysRemaining <= 30 ? 'text-orange-300' : 'text-yellow-300';

              // Check for planned training for this non-role qualification
              const nonRolePlannedTraining = trainingEvents.find(event =>
                event.userId === userId &&
                event.qualificationId === q.qualification_type_data?.id &&
                new Date(event.start_date) > today &&
                (event.status === 'planned' || event.status === 'approved')
              );

              const trainingInfo = nonRolePlannedTraining ? ` (training planned on ${new Date(nonRolePlannedTraining.start_date).toLocaleDateString()})` : '';

              return `<li class="${colorClass}">${q.qualification_type_data?.name || 'No data'} - ${statusText}${trainingInfo}</li>`;
            }).join('')}
          </ul>
        </div>
      `;
    }

    return {
      color,
      hoverColor,
      tooltip
    };
  };

  const fetchData = useCallback(async (forceReloadAll = false) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("access_token");
      if (!token) {
        toast.error("No authentication token found");
        return;
      }

      let startDate, endDate;

      if (isCustomDateRange && customStartDate && customEndDate) {
        startDate = new Date(customStartDate);
        endDate = new Date(customEndDate);
      } else {
        startDate = new Date(scrollableCalendarStart);
        endDate = new Date(scrollableCalendarEnd);
      }

      // ========== CALL OPTIMIZED ENDPOINT ==========
      const response = await axios.get(`${API_BASE_URL}/training-calendar-data/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          start_date: startDate.toISOString().split("T")[0],
          end_date: endDate.toISOString().split("T")[0]
        }
      });

      const { users: usersData, trainings: trainingsData, events_index, meta } = response.data;

      // ========== PROCESS USERS ==========
      // Transform users to match existing format
      const processedUsers = usersData.map(user => ({
        id: user.id,
        username: user.username,
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        hidden_on_board: user.hidden_on_board,
        training_role: user.training_role?.id,
        training_role_data: user.training_role ? {
          id: user.training_role.id,
          name: user.training_role.name,
          qualificationmatrix_data: (user.role_status?.required_qualifications || []).map(qual => ({
            qualification_type: {
              id: qual.id,
              name: qual.name
            },
            has_qualification: qual.has_qualification
          }))
        } : null,
        training_site: user.training_site?.id,
        training_site_data: user.training_site,
        training_country: user.training_country?.id,
        training_country_data: user.training_country,
        training_supervisor: user.training_supervisor?.id,
        training_supervisor_data: user.training_supervisor,
        training_coordinator: user.training_coordinator?.id,
        training_coordinator_data: user.training_coordinator,
        manager: user.manager?.id,
        manager_data: user.manager,
        // Add qualifications in the format expected by existing code
        qualifications: (user.qualifications || []).map(qual => ({
          id: qual.id,
          qualification_type: qual.qualification_type_id,
          qualification_type_data: {
            id: qual.qualification_type_id,
            name: qual.qualification_type_name
          },
          expiry_date: qual.expiry_date,
          days_until_expiry: qual.days_until_expiry,
          is_expired: qual.is_expired,
          certificate_number: qual.certificate_number,
          user: user.id
        }))
      }));

      setUsers(processedUsers);

      // ========== PROCESS TRAININGS ==========
      // Convert trainings back to expected format (dates as strings, participant_ids as users_data)
      const processedTrainings = trainingsData.map(training => ({
        id: training.id,
        name: training.name,
        start_date: training.start_date,
        end_date: training.end_date,
        status: training.status,
        availability: training.availability,
        users_data: training.participant_ids,  // Just IDs for now
        participants_count: training.participants_count,
        created_by: training.created_by,
        qualification_name: training.qualification_name,
        training_center_name: training.training_center_name,
        // Training Center Qualification ID - needed for editing
        training_center_qualification_id: training.training_center_qualification_id,
        training_center_qualification: training.training_center_qualification_id,
        training_center_qualification_data: {
          id: training.training_center_qualification_id,
          training_center: {
            id: training.training_center_id,
            name: training.training_center_name
          },
          qualification_type: {
            qualification_type_data: {
              id: training.qualification_type_id,
              name: training.qualification_name
            }
          }
        }
      }));

      setTrainings(processedTrainings);
      setLoadedTrainingsDateRange({
        start: startDate,
        end: endDate
      });

      // ========== PROCESS TRAINING EVENTS ==========
      // Convert from events_index to trainingEvents array
      const trainingEvents = [];
      Object.entries(events_index).forEach(([key, events]) => {
        // Key format: "{user_id}-{YYYY-MM-DD}" e.g., "3-2025-11-19"
        // Split only on the first hyphen to separate userId from date
        const firstHyphenIndex = key.indexOf('-');
        const userIdStr = key.substring(0, firstHyphenIndex);
        const dateStr = key.substring(firstHyphenIndex + 1);
        const userId = parseInt(userIdStr);

        events.forEach(event => {
          trainingEvents.push({
            id: `training-${event.training_id}-${userId}`,
            userId: userId,
            trainingId: event.training_id,
            trainingName: event.training_name,
            description: event.training_name,
            // Add time to ensure date is parsed as local time, not UTC
            start_date: dateStr,
            end_date: dateStr,
            type: "training",
            status: event.status || 'planned',
            qualificationId: event.qualification_id,
            qualificationName: event.qualification_name,
            training_center_name: event.training_center_name,
            location: event.training_center_name || "Training Center"
          });
        });
      });

      // ========== PROCESS EXPIRING QUALIFICATIONS ==========
      const warningEvents = [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const allUserQualifications = [];

      processedUsers.forEach(user => {
        if (user.qualifications && Array.isArray(user.qualifications)) {
          user.qualifications.forEach(qual => {
            // Add to allUserQualifications for compatibility
            allUserQualifications.push({
              id: qual.id,
              user: user.id,
              qualification_type: qual.qualification_type,
              qualification_type_data: qual.qualification_type_data,
              expiry_date: qual.expiry_date,
              certificate_number: qual.certificate_number,
              days_until_expiry: qual.days_until_expiry,
              is_expired: qual.is_expired
            });

            // Create warning events for expiring/expired qualifications
            if (qual.expiry_date) {
              const expiryDate = new Date(qual.expiry_date);
              expiryDate.setHours(0, 0, 0, 0);
              const daysRemaining = Math.floor((expiryDate - today) / (1000 * 60 * 60 * 24));

              warningEvents.push({
                id: `qualificationalert-${qual.id}`,
                qualificationId: qual.id,
                userId: user.username,
                trainingName: qual.qualification_type_data?.name || "Unnamed qualification",
                description: qual.qualification_type_data?.name || "No description",
                expiryDate: qual.expiry_date,
                daysRemaining: daysRemaining,
                certificate_number: qual.certificate_number || "",
                type: "qualificationalert",
                start_date: qual.expiry_date,
                end_date: qual.expiry_date,
              });
            }
          });
        }
      });

      setUserExpirations(warningEvents);
      setUserQualifications(allUserQualifications);

      // ========== PROCESS QUALIFICATION TYPES ==========
      // Extract unique qualification types from users' qualifications
      const qualificationTypesMap = new Map();
      processedUsers.forEach(user => {
        if (user.qualifications) {
          user.qualifications.forEach(qual => {
            if (qual.qualification_type_data?.id && !qualificationTypesMap.has(qual.qualification_type_data.id)) {
              qualificationTypesMap.set(qual.qualification_type_data.id, {
                id: qual.qualification_type_data.id,
                name: qual.qualification_type_data.name
              });
            }
          });
        }
      });

      setQualificationTypes(Array.from(qualificationTypesMap.values()));

      // ========== CALCULATE QUALIFICATION STATUS ==========
      const qualificationStatus = {};
      processedUsers.forEach(user => {
        // Use the full getUserQualificationStatus function which generates tooltip, color, etc.
        qualificationStatus[user.id] = getUserQualificationStatus(user.id, allUserQualifications, trainingEvents, user);
      });
      setUserQualificationStatus(qualificationStatus);

      // ========== TRAINING OPTIONS (for dropdown) ==========
      // This would need a separate endpoint or be included in response
      // For now, keep it empty or fetch separately if needed
      setTrainingOptions([]);

      // ========== SET USER WORK (EMPTY) ==========
      setUserWork([]);

      // ========== SET USER EVENTS ==========
      const allEvents = [...trainingEvents];
      setUserEvents(allEvents);

    } catch (error) {
      console.error("❌ [MainboardPanel] Error fetching data:", error);

      // Detailed error logging
      if (error.response) {
        console.error("📡 [MainboardPanel] API Error Response:", {
          status: error.response.status,
          statusText: error.response.statusText,
          url: error.response.config?.url,
          method: error.response.config?.method?.toUpperCase(),
          data: error.response.data
        });
      } else if (error.request) {
        console.error("📡 [MainboardPanel] Network Error - No Response:", {
          url: error.request.responseURL || error.config?.url,
          method: error.config?.method?.toUpperCase()
        });
      } else {
        console.error("📡 [MainboardPanel] Request Setup Error:", error.message);
      }

      toast.error("Error loading data: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  }, [isCustomDateRange, customStartDate, customEndDate, scrollableCalendarStart, scrollableCalendarEnd]);

  useEffect(() => {
    // Fetch data when custom date range changes
    if (isCustomDateRange && customStartDate && customEndDate) {
      console.log('📅 [MainboardPanel] Custom date range changed - forcing data reload:', {
        customStartDate,
        customEndDate,
        isCustomDateRange
      });
      fetchData(true); // Force reload for custom date range
    }
  }, [isCustomDateRange, customStartDate, customEndDate, fetchData]);

  useEffect(() => {
    // Fetch data when scrollable calendar changes
    if (!isCustomDateRange) {
      console.log('📅 [MainboardPanel] Scrollable calendar range changed - fetching data:', {
        scrollableCalendarStart: scrollableCalendarStart.toISOString().split('T')[0],
        scrollableCalendarEnd: scrollableCalendarEnd.toISOString().split('T')[0],
        isCustomDateRange
      });
      fetchData();
    }
  }, [scrollableCalendarStart, scrollableCalendarEnd, isCustomDateRange, fetchData]);


  const handleZoomIn = useCallback(() => setZoomLevel((prev) => Math.min(prev + 0.25, 2.0)), []);
  const handleZoomOut = useCallback(() => setZoomLevel((prev) => Math.max(prev - 0.25, 0.5)), []);

  const handleDayClick = useCallback((person, date) => setSelectedUserDay({ person, date }), []);

  const handleOrderDoubleClick = useCallback((person, date, item, options) => {
    if (options?.openQualification && options?.qualificationId) {
      const qualification = userQualifications.find(q => q.id === options.qualificationId);
      if (qualification) {
        setEditingQualification(qualification);
      } else {
        setSelectedUserDay({ person, date });
      }
    } else {
      setSelectedUserDay({ person, date });
    }
  }, [userQualifications]);

  const handleEditTraining = useCallback((person, date, trainingEvent) => {
    if (!trainingEvent || !trainingEvent.originalTrainingId) {
      toast.error('Invalid training data');
      return;
    }

    const trainingId = trainingEvent.originalTrainingId;
    const originalTraining = trainings.find(t => t.id == trainingId);

    if (originalTraining) {
      setEditingTraining(originalTraining);
    } else {
      toast.error(`Could not find training with ID ${trainingId}`);
    }
  }, [trainings]);

  const handleMailReminder = useCallback((training) => {
    setMailSenderData({
      training: training,
      statusChange: {
        type: 'reminder',
        from: training.status,
        to: training.status
      }
    });
  }, []);

  const handleSelectTraining = useCallback((item) => {
    setSelectedTrainings(prev => {
      const exists = prev.some(t => t.id === item.id);
      if (exists) {
        return prev.filter(t => t.id !== item.id);
      } else {
        return [...prev, item];
      }
    });
  }, []);

  const handleStatusChange = async (trainingId, newStatus, oldStatus, trainingData) => {
    try {
      const token = localStorage.getItem("access_token");
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      await axios.patch(
        `${API_BASE_URL}/trainings/${trainingId}/`,
        { status: newStatus },
        config
      );

      console.log('🔄 [MainboardPanel] Training status updated - forcing full data reload:', {
        trainingId,
        oldStatus,
        newStatus
      });
      fetchData(true); // Force reload after status change
      toast.success("Training status updated successfully");

      if ((oldStatus === 'planned' && newStatus === 'approved') || 
          (oldStatus === 'approved' && newStatus === 'cancelled')) {
        setMailSenderData({
          training: trainingData,
          statusChange: {
            from: oldStatus,
            to: newStatus
          }
        });
      }
    } catch (error) {
      console.error("Error updating training status:", error);
      toast.error("Failed to update training status");
    }
  };

  const handleDeleteMultipleItems = async () => {
    if (selectedTrainings.length === 0) {
      toast.error("No items selected");
      return;
    }

    const trainings = selectedTrainings.filter(item => item.type === 'training');
    
    let confirmMessage = 'Are you sure you want to remove ';
    if (trainings.length > 0) {
      confirmMessage += `${trainings.length} training assignment${trainings.length > 1 ? 's' : ''}? This will remove the selected participants from their trainings.`;
    } else {
      confirmMessage += 'selected items?';
    }

    const confirmDelete = window.confirm(confirmMessage);

    if (!confirmDelete) {
      return;
    }

    try {
      const token = localStorage.getItem("access_token");
      
      const trainingGroups = {};
      trainings.forEach(item => {
        const originalTrainingId = item.originalTrainingId;
        const userId = item.userId;
        
        if (!trainingGroups[originalTrainingId]) {
          trainingGroups[originalTrainingId] = [];
        }
        trainingGroups[originalTrainingId].push(userId);
      });

      const promises = Object.entries(trainingGroups).map(([trainingId, userIds]) => {
        return Promise.all(userIds.map(userId => 
          axios.post(
            `${API_BASE_URL}/trainings/${trainingId}/remove_participant/`,
            { user_id: userId },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json"
              }
            }
          )
        ));
      }).flat();

      await Promise.all(promises);
      
      let successMessage = 'Successfully removed ';
      if (trainings.length > 0) {
        successMessage += `${trainings.length} participant${trainings.length > 1 ? 's' : ''} from training${trainings.length > 1 ? 's' : ''}`;
      } else {
        successMessage += 'selected items';
      }
      
      toast.success(successMessage);
      setSelectedTrainings([]);
      setIsMultiSelectMode(false);
      fetchData(true); // Force reload after deleting items
    } catch (error) {
      console.error("Error deleting items:", error);
      toast.error("Failed to delete some items");
    }
  };

  const formatDateRange = (startDate) => {
    let endDate;
    
    if (isCustomDateRange && customStartDate && customEndDate) {
      endDate = new Date(customEndDate);
    } else {
      endDate = new Date(scrollableCalendarEnd);
    }
    
    const dateRangeText = `${startDate.toLocaleDateString("en-US", { day: "numeric", month: "long" })} - ${endDate.toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" })}`;
    
    return isCustomDateRange ? `${dateRangeText} (Custom)` : dateRangeText;
  };

  const formatDayDisplay = (day, index) => {
    const isToday = day.toDateString() === new Date().toDateString();
    return (
      <div className="text-xs font-medium text-slate-700 flex flex-col items-center">
        <span className="text-[9px] text-slate-500">{day.toLocaleDateString("en-US", { weekday: "short" })}</span>
        <span className={`text-sm ${isToday ? "font-bold text-brand-500" : ""}`}>{day.getDate()}</span>
        <span className="text-[9px] text-slate-500">{day.toLocaleDateString("en-US", { month: "short" })}</span>
      </div>
    );
  };

  // Memoized unique filter lists
  const uniqueRoles = useMemo(() => {
    return users
      .filter(user => user.training_role_data && user.training_role_data.name)
      .map(user => user.training_role_data)
      .filter((role, index, self) =>
        index === self.findIndex(r => r.id === role.id)
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [users]);

  const uniqueSites = useMemo(() => {
    return users
      .filter(user => user.training_site_data && user.training_site_data.name)
      .map(user => user.training_site_data)
      .filter((site, index, self) =>
        index === self.findIndex(s => s.id === site.id)
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [users]);

  const uniqueCountries = useMemo(() => {
    return users
      .filter(user => user.training_country_data && user.training_country_data.name)
      .map(user => user.training_country_data)
      .filter((country, index, self) =>
        index === self.findIndex(c => c.id === country.id)
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [users]);

  const uniqueManagers = useMemo(() => {
    return users
      .filter(user => user.manager_data && user.manager_data.first_name)
      .map(user => user.manager_data)
      .filter((manager, index, self) =>
        index === self.findIndex(m => m.id === manager.id)
      )
      .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`));
  }, [users]);

  const uniqueSupervisors = useMemo(() => {
    return users
      .filter(user => user.training_supervisor_data && user.training_supervisor_data.first_name)
      .map(user => user.training_supervisor_data)
      .filter((supervisor, index, self) =>
        index === self.findIndex(s => s.id === supervisor.id)
      )
      .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`));
  }, [users]);

  const uniqueCoordinators = useMemo(() => {
    return users
      .filter(user => user.training_coordinator_data && user.training_coordinator_data.first_name)
      .map(user => user.training_coordinator_data)
      .filter((coordinator, index, self) =>
        index === self.findIndex(c => c.id === coordinator.id)
      )
      .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`));
  }, [users]);

  const activeUsers = useMemo(() => {
    let filteredUsers = users;

    if (activeFilters.selectedRoles.length > 0) {
      filteredUsers = filteredUsers.filter(user =>
        user.training_role_data && activeFilters.selectedRoles.includes(user.training_role_data.id)
      );
    }

    if (activeFilters.selectedSites.length > 0) {
      filteredUsers = filteredUsers.filter(user =>
        user.training_site_data && activeFilters.selectedSites.includes(user.training_site_data.id)
      );
    }

    if (activeFilters.selectedCountries.length > 0) {
      filteredUsers = filteredUsers.filter(user =>
        user.training_country_data && activeFilters.selectedCountries.includes(user.training_country_data.id)
      );
    }

    if (activeFilters.selectedManagers.length > 0) {
      filteredUsers = filteredUsers.filter(user =>
        user.manager_data && activeFilters.selectedManagers.includes(user.manager_data.id)
      );
    }

    if (activeFilters.selectedSupervisors.length > 0) {
      filteredUsers = filteredUsers.filter(user =>
        user.training_supervisor_data && activeFilters.selectedSupervisors.includes(user.training_supervisor_data.id)
      );
    }

    if (activeFilters.selectedCoordinators.length > 0) {
      filteredUsers = filteredUsers.filter(user =>
        user.training_coordinator_data && activeFilters.selectedCoordinators.includes(user.training_coordinator_data.id)
      );
    }

    if (activeFilters.searchTerm) {
      const searchLower = activeFilters.searchTerm.toLowerCase();
      filteredUsers = filteredUsers.filter((user) => {
        const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
        const username = user.username ? user.username.toLowerCase() : '';
        const roleName = user.training_role_data?.name ? user.training_role_data.name.toLowerCase() : '';

        const matchesUser = fullName.includes(searchLower) ||
                           username.includes(searchLower) ||
                           roleName.includes(searchLower);

        const startDate = daysForView[0];
        const endDate = daysForView[daysForView.length - 1];

        const userRelevantEvents = userEvents.filter((event) => {
          if (!event.start_date && !event.planned_date_start) return false;
          const eventDate = new Date(event.start_date || event.planned_date_start);
          return event.userId === user.id && eventDate >= startDate && eventDate <= endDate;
        });

        const userRelevantExpirations = userExpirations.filter((expiration) => {
          if (!expiration.expiryDate) return false;
          const expiryDate = new Date(expiration.expiryDate);
          return expiration.userId === user.username && expiryDate >= startDate && expiryDate <= endDate;
        });

        const matchesEvents = userRelevantEvents.some((event) => {
          if (event.type === "training") {
            return (
              (event.trainingName || "").toLowerCase().includes(searchLower) ||
              (event.qualificationName || "").toLowerCase().includes(searchLower) ||
              (event.description || "").toLowerCase().includes(searchLower) ||
              (event.location || "").toLowerCase().includes(searchLower)
            );
          } else if (event.type === "travel") {
            return (
              (event.address || "").toLowerCase().includes(searchLower) ||
              (event.location || "").toLowerCase().includes(searchLower) ||
              (event.comments || "").toLowerCase().includes(searchLower)
            );
          }
          return false;
        });

        const matchesExpirations = userRelevantExpirations.some((expiration) => {
          return (
            (expiration.trainingName || "").toLowerCase().includes(searchLower) ||
            (expiration.description || "").toLowerCase().includes(searchLower) ||
            (expiration.certificate_number || "").toLowerCase().includes(searchLower)
          );
        });

        const finalMatch = matchesUser || matchesEvents || matchesExpirations;
        return finalMatch;
      });
    }

    if (activeFilters.showCertificateIssues) {
      filteredUsers = filteredUsers.filter((user) => {
        const roleQualifications = user.training_role_data?.qualificationmatrix_data || [];

        // If user has no role or role has no required qualifications, exclude them
        if (!roleQualifications.length) {
          return false;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const allUserQualifications = userQualifications.filter(q => q.user === user.id);

        // Check for missing certificates WITHOUT planned training
        const missingQualifications = roleQualifications.filter(roleQual => {
          const hasQualification = allUserQualifications.some(userQual =>
            userQual.qualification_type_data?.id === roleQual.qualification_type.id
          );

          if (!hasQualification) {
            // Check if there's a planned or approved training for this qualification
            const hasPlannedTraining = userEvents.some(event =>
              event.userId === user.id &&
              event.qualificationId === roleQual.qualification_type.id &&
              new Date(event.start_date) > today &&
              (event.status === 'planned' || event.status === 'approved')
            );

            // Only count as missing if there's NO planned training
            return !hasPlannedTraining;
          }

          return false;
        });

        // Check for expiring/expired certificates within role requirements
        const userRoleQualifications = allUserQualifications.filter(userQual =>
          roleQualifications.some(roleQual =>
            roleQual.qualification_type.id === userQual.qualification_type_data?.id
          )
        );

        const expiringQualifications = userRoleQualifications.filter(qualification => {
          if (qualification.expiry_date) {
            const expiryDate = new Date(qualification.expiry_date);
            expiryDate.setHours(0, 0, 0, 0);
            const daysRemaining = Math.floor((expiryDate - today) / (1000 * 60 * 60 * 24));

            // Consider expired or expiring within 60 days as potential issues
            if (daysRemaining <= 60) {
              // Check if there's a planned or approved training for this qualification
              const hasPlannedTraining = userEvents.some(event =>
                event.userId === user.id &&
                event.qualificationId === qualification.qualification_type_data?.id &&
                new Date(event.start_date) > today &&
                (event.status === 'planned' || event.status === 'approved')
              );

              // Only count as an issue if there's NO planned training
              return !hasPlannedTraining;
            }
          }
          return false;
        });

        // Show user if they have missing qualifications OR expiring/expired qualifications
        // (both WITHOUT planned trainings)
        return missingQualifications.length > 0 || expiringQualifications.length > 0;
      });
    }

    return filteredUsers.sort((a, b) =>
      `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`)
    );
  }, [users, activeFilters, daysForView, userEvents, userExpirations, userQualifications]);

  const trainingsList = useMemo(() => {
    return trainings
      .filter(training => {
        if (activeFilters.searchTerm) {
          const searchLower = activeFilters.searchTerm.toLowerCase();
          const trainingName = training.name ? training.name.toLowerCase() : '';
          const qualificationName = training.training_center_qualification_data?.qualification_type?.qualification_type_data?.name ?
            training.training_center_qualification_data.qualification_type.qualification_type_data.name.toLowerCase() : '';
          const centerName = training.training_center_qualification_data?.training_center?.name ?
            training.training_center_qualification_data.training_center.name.toLowerCase() : '';

          if (!(trainingName.includes(searchLower) ||
                qualificationName.includes(searchLower) ||
                centerName.includes(searchLower))) {
            return false;
          }
        }

        if (activeFilters.selectedQualifications.length > 0) {
          const qualificationId = training.training_center_qualification_data?.qualification_type?.qualification_type_data?.id;
          if (!qualificationId || !activeFilters.selectedQualifications.includes(qualificationId)) {
            return false;
          }
        }

        if (activeFilters.selectedTrainingCenters.length > 0) {
          const trainingCenterId = training.training_center_qualification_data?.training_center?.id;
          if (!trainingCenterId || !activeFilters.selectedTrainingCenters.includes(trainingCenterId)) {
            return false;
          }
        }

        if (activeFilters.selectedCreatedBy.length > 0) {
          const createdBy = training.created_by || '';
          if (!activeFilters.selectedCreatedBy.includes(createdBy)) {
            return false;
          }
        }

        if (activeFilters.selectedStatuses.length > 0) {
          const trainingStatus = training.status || '';
          if (!activeFilters.selectedStatuses.includes(trainingStatus)) {
            return false;
          }
        }

        if (activeFilters.trainingDateRange.start || activeFilters.trainingDateRange.end) {
          const trainingStartDate = new Date(training.start_date);
          const trainingEndDate = new Date(training.end_date);

          if (activeFilters.trainingDateRange.start) {
            const filterStartDate = new Date(activeFilters.trainingDateRange.start);
            if (trainingEndDate < filterStartDate) {
              return false;
            }
          }

          if (activeFilters.trainingDateRange.end) {
            const filterEndDate = new Date(activeFilters.trainingDateRange.end);
            filterEndDate.setHours(23, 59, 59, 999);
            if (trainingStartDate > filterEndDate) {
              return false;
            }
          }
        }

        return true;
      })
      .map(training => ({
        ...training,
        // Use participants_count from API if available, otherwise calculate from users_data
        participants_count: training.participants_count ?? (training.users_data ? training.users_data.length : 0),
        qualification_name: training.qualification_name || training.training_center_qualification_data?.qualification_type?.qualification_type_data?.name || 'Unknown',
        training_center_name: training.training_center_name || training.training_center_qualification_data?.training_center?.name || 'Unknown',
        availability: training.availability || training.training_center_qualification_data?.availability
      }))
      .sort((a, b) => {
        const nameA = a.name || a.qualification_name;
        const nameB = b.name || b.qualification_name;
        return nameA.localeCompare(nameB);
      });
  }, [trainings, activeFilters]);

  // Item data for virtualized list
  const listItemData = useMemo(() => ({
    items: viewType === 'users' ? (activeUsers || []) : (trainingsList || []),
    daysForView: daysForView || [],
    userWork: userWork || [],
    userEvents: userEvents || [],
    userExpirations: userExpirations || [],
    qualificationTypes: qualificationTypes || [],
    trainings: trainings || [],
    handleDayClick,
    handleOrderDoubleClick,
    handleEditTraining,
    viewType,
    userQualificationStatus: userQualificationStatus || {},
    getRoleTooltip,
    draggedOverUser,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleFileDrop,
    setEditingUserId,
    setIsUserFormOpen,
    setEditingTraining,
    setSelectedUserDay,
    columnWidth
  }), [
    viewType, activeUsers, trainingsList, daysForView, userWork, userEvents,
    userExpirations, qualificationTypes, trainings, handleDayClick,
    handleOrderDoubleClick, handleEditTraining, userQualificationStatus,
    draggedOverUser, handleDragOver, handleDragEnter, handleDragLeave,
    handleFileDrop, columnWidth
  ]);

  return (
    <div className="min-h-screen bg-steel-50">
      <Tooltip.Provider>
        <div className="h-screen bg-white border border-steel-200 flex flex-col">
          <HeaderToolbar
            viewType={viewType}
            setViewType={setViewType}
            displayMode={displayMode}
            setDisplayMode={setDisplayMode}
            isCustomDateRange={isCustomDateRange}
            setIsDateRangePickerOpen={setIsDateRangePickerOpen}
            setIsCustomDateRange={setIsCustomDateRange}
            setCustomStartDate={setCustomStartDate}
            setCustomEndDate={setCustomEndDate}
            setEditingTraining={setEditingTraining}
            navigate={navigate}
            toggleFullscreen={toggleFullscreen}
            isFullscreen={isFullscreen}
            setSidebarOpen={setSidebarOpen}
            activeFilters={activeFilters}
            onFiltersChange={handleFiltersChange}
            scrollableCalendarStart={scrollableCalendarStart}
            scrollableCalendarEnd={scrollableCalendarEnd}
            onNavigateBack={handleNavigateBack}
            onNavigateForward={handleNavigateForward}
            onNavigateToday={handleNavigateToday}
          />
          <div className="p-0 flex-1 flex flex-col min-h-0">
            {loading ? (
              <div className="py-8 flex flex-col items-center justify-center">
                <p className="text-slate-600 text-sm">Loading calendar...</p>
              </div>
            ) : displayMode === 'table' ? (
              <div className="p-4">
                <TrainingsTable
                  trainings={trainingsList}
                  onEditTraining={setEditingTraining}
                  onStatusChange={handleStatusChange}
                  onMailReminder={handleMailReminder}
                />
              </div>
            ) : (
              <div
                className="relative overflow-x-auto overflow-y-auto flex-1"
                ref={scrollableParentRef}
                onScroll={(e) => setScrollTop(e.target.scrollTop)}
              >
                <div className="relative" style={{ minWidth: gridMinWidth }}>
                  <div
                    className="inline-grid relative"
                    style={gridStyle}
                  >
                    {/* Header - Users/Trainings switcher */}
                    <div className="sticky top-0 left-0 z-40 bg-steel-100 font-semibold border-b border-steel-200">
                      <div className="w-full h-full p-3 flex items-center justify-center">
                        <nav className="flex space-x-2 w-full">
                          {['users', 'trainings'].map((opt) => (
                            <button
                              key={opt}
                              onClick={() => {
                                setViewType(opt);
                                if (opt === 'users') {
                                  setDisplayMode('trainings');
                                } else if (opt === 'trainings') {
                                  setDisplayMode('table');
                                }
                              }}
                              className={`flex-1 py-2 px-4 text-sm font-medium border-b-2 transition-colors ${viewType === opt ? 'border-brand-500 text-brand-500' : 'border-transparent text-slate-400 hover:text-slate-500'}`}
                            >
                              {opt === 'users' ? 'Users' : 'Trainings'}
                            </button>
                          ))}
                        </nav>
                      </div>
                    </div>

                    {/* Day headers */}
                    {daysForView.map((day, index) => {
                      const isToday = day.toDateString() === new Date().toDateString();
                      const isWeekend = day.getDay() === 0;
                      return (
                        <div
                          key={index}
                          className={`sticky top-0 z-30 bg-steel-100 p-1 border-b border-steel-200 ${isWeekend ? "bg-red-50" : isToday ? "bg-brand-50 border border-brand-400" : ""}`}
                        >
                          {formatDayDisplay(day, index)}
                        </div>
                      );
                    })}

                    {/* Virtualized Rows */}
                    {(() => {
                      const items = listItemData.items;
                      const totalRows = items.length;
                      const containerHeight = scrollableParentRef.current?.clientHeight || 800;
                      const visibleRows = Math.ceil(containerHeight / ROW_HEIGHT);
                      const startIndex = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
                      const endIndex = Math.min(totalRows, startIndex + visibleRows + OVERSCAN * 2);

                      // Top padding to maintain scroll position
                      const topPadding = startIndex * ROW_HEIGHT;
                      // Bottom padding to maintain total scroll height
                      const bottomPadding = (totalRows - endIndex) * ROW_HEIGHT;

                      return (
                        <>
                          {/* Top spacer */}
                          {topPadding > 0 && (
                            <div style={{ gridColumn: `1 / -1`, height: topPadding }} />
                          )}

                          {/* Visible rows */}
                          {items.slice(startIndex, endIndex).map((item) => (
                            <React.Fragment key={item.id}>
                        {/* User column - sticky */}
                        <Tooltip.Root>
                          <Tooltip.Trigger asChild>
                            <div
                              className={`sticky left-0 z-20 h-[50px] ${
                                viewType === 'users' && draggedOverUser === item.id
                                  ? 'bg-brand-100 border-brand-400'
                                  : viewType === 'users' ? (userQualificationStatus[item.id]?.color || 'bg-steel-50') : 'bg-steel-50'
                              } p-2 border-b border-r border-steel-200 cursor-pointer transition-colors relative`}
                              onDoubleClick={() => {
                                if (viewType === 'users') {
                                  setSelectedUserDay({ person: item, date: new Date(), isUserColumnClick: true });
                                } else {
                                  setEditingTraining(item);
                                }
                              }}
                              onDragOver={viewType === 'users' ? handleDragOver : undefined}
                              onDragEnter={viewType === 'users' ? (e) => handleDragEnter(e, item.id) : undefined}
                              onDragLeave={viewType === 'users' ? handleDragLeave : undefined}
                              onDrop={viewType === 'users' ? (e) => handleFileDrop(e, item.id) : undefined}
                            >
                              <div className="w-full h-full cursor-pointer flex items-center justify-between relative">
                                <div className="ml-2 font-medium text-xs text-slate-900 truncate">
                                  {viewType === 'users' ? (
                                    <>
                                      <div className="font-medium">
                                        {item.first_name} {item.last_name}
                                      </div>
                                      <div className={`text-xs font-medium ${
                                        item.training_role_data ? 'text-brand-500' : 'text-slate-500'
                                      }`}>
                                        {item.training_role_data ? item.training_role_data.name : 'No assigned role'}
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <div className="font-medium">{item.name}</div>
                                      <div className="text-slate-600 text-xs">{item.qualification_name}</div>
                                    </>
                                  )}
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (viewType === 'users') {
                                      setEditingUserId(item.id);
                                      setIsUserFormOpen(true);
                                    } else {
                                      setEditingTraining(item);
                                    }
                                  }}
                                  className="p-0.5 rounded-xl hover:bg-brand-50 text-brand-500 hover:text-brand-600 absolute top-1 right-1"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                              </div>

                              {viewType === 'users' && draggedOverUser === item.id && (
                                <div className="absolute inset-0 bg-brand-100 bg-opacity-75 border-2 border-brand-400 border-dashed rounded-xl flex items-center justify-center z-20">
                                  <div className="bg-white rounded-xl px-3 py-1 shadow-lg">
                                    <div className="flex items-center text-brand-600 text-xs font-medium">
                                      <FileText className="h-4 w-4 mr-1" />
                                      Drop certificate files here
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </Tooltip.Trigger>
                          <Tooltip.Portal>
                            <Tooltip.Content
                              className="bg-slate-800 text-white text-xs rounded-xl p-2 shadow-lg max-w-xl"
                              side="right"
                              sideOffset={5}
                            >
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: viewType === 'users'
                                    ? (userQualificationStatus[item.id]?.tooltip || "No qualification data")
                                    : `<strong>${item.name || item.qualification_name}</strong>`
                                }}
                              />
                              <Tooltip.Arrow className="fill-slate-800" />
                            </Tooltip.Content>
                          </Tooltip.Portal>
                        </Tooltip.Root>

                        {/* Calendar cells */}
                        {daysForView.map((day, dayIndex) => (
                          <CalendarCell
                            key={dayIndex}
                            person={item}
                            day={day}
                            userWork={userWork}
                            userEvents={userEvents}
                            userExpirations={userExpirations}
                            qualificationTypes={qualificationTypes}
                            trainings={trainings}
                            onDayClick={handleDayClick}
                            onOrderDoubleClick={handleOrderDoubleClick}
                            onEditTraining={handleEditTraining}
                            viewType={viewType}
                          />
                        ))}
                      </React.Fragment>
                          ))}

                          {/* Bottom spacer */}
                          {bottomPadding > 0 && (
                            <div style={{ gridColumn: `1 / -1`, height: bottomPadding }} />
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}
          </div>

          {selectedUserDay && (
            <UserDayView
              user={selectedUserDay.person}
              date={selectedUserDay.date}
              userWork={userWork}
              userEvents={userEvents}
              userExpirations={userExpirations}
              trainingOptions={trainingOptions}
              userQualifications={userQualifications}
              companyId={companyId}
              onClose={() => setSelectedUserDay(null)}
              fetchData={fetchData}
              isUserColumnClick={selectedUserDay.isUserColumnClick}
            />
          )}
          {editingTraining && (
            <TrainingEditModal
              isOpen={!!editingTraining}
              onClose={() => setEditingTraining(null)}
              trainingData={editingTraining}
              fetchData={fetchData}
            />
          )}
          {editingMultiple && (
            <BulkEditDialog
              isOpen={!!editingMultiple}
              onClose={() => setEditingMultiple(null)}
              data={editingMultiple}
              trainingOptions={trainingOptions}
              fetchData={fetchData}
              setSelectedTrainings={setSelectedTrainings}
              setIsMultiSelectMode={setIsMultiSelectMode}
            />
          )}
          {editingQualification && (
            <EditQualificationDialog
              isOpen={!!editingQualification}
              onClose={() => setEditingQualification(null)}
              qualificationData={editingQualification}
              fetchData={fetchData}
            />
          )}
          
          {mailSenderData && (
            <TrainingMailSender
              isOpen={!!mailSenderData}
              onClose={() => setMailSenderData(null)}
              trainingData={mailSenderData.training}
              statusChange={mailSenderData.statusChange}
              onSuccess={(result) => {
                const participantsSent = result.results?.participants_sent || 0;
                const supervisorsSent = result.results?.supervisors_sent || 0;
                const trainingCenterSent = result.results?.training_center_sent || 0;
                const totalSent = participantsSent + supervisorsSent + trainingCenterSent;
                
                toast.success(`Emails sent successfully! Participants: ${participantsSent}, Supervisors: ${supervisorsSent}, Training Center: ${trainingCenterSent} (Total: ${totalSent})`);
                
                fetchData(true); // Force reload after sending emails
              }}
            />
          )}
          
          <Dialog open={isDateRangePickerOpen} onOpenChange={(open) => {
            if (!open) {
              setIsDateRangePickerOpen(false);
            }
          }}>
            <DialogContent className="sm:max-w-[400px] rounded-2xl">
              <DialogHeader>
                <DialogTitle className="flex items-center">
                  <Calendar className="h-5 w-5 mr-2 text-brand-500" />
                  Select Custom Date Range
                </DialogTitle>
                <p className="text-sm text-slate-600 mt-1">
                  Choose any date range - past, present, or future
                </p>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      placeholder="Select start date"
                      className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      min={customStartDate}
                      placeholder="Select end date"
                      className="w-full px-3 py-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                    />
                  </div>
                </div>
                
                {customStartDate && customEndDate && (() => {
                  const totalDays = Math.ceil((new Date(customEndDate) - new Date(customStartDate)) / (1000 * 60 * 60 * 24)) + 1;
                  return (
                    <>
                      <div className="bg-brand-50 border border-brand-200 rounded-xl p-3">
                        <p className="text-sm text-brand-700">
                          <strong>Selected Range:</strong> {new Date(customStartDate).toLocaleDateString()} - {new Date(customEndDate).toLocaleDateString()}
                        </p>
                        <p className="text-xs text-brand-500 mt-1">
                          Total days: {totalDays}
                        </p>
                      </div>
                      {totalDays > 70 && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3">
                          <p className="text-sm text-yellow-800">
                            <strong>Warning:</strong> Selecting such a long date range may affect performance and slow down the application.
                          </p>
                        </div>
                      )}
                    </>
                  );
                })()}
                
                {isCustomDateRange && (
                  <div className="bg-brand-50 border border-brand-200 rounded-xl p-3">
                    <p className="text-sm text-brand-700">
                      <strong>Currently Active:</strong> Custom date range is being used
                    </p>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsDateRangePickerOpen(false);
                  }}
                >
                  Cancel
                </Button>
                {isCustomDateRange && (
                  <Button
                    variant="outline"
                    onClick={handleDateRangeReset}
                  >
                    Reset to Default
                  </Button>
                )}
                <Button
                  onClick={handleDateRangeApply}
                  disabled={!customStartDate || !customEndDate}
                >
                  Apply Range
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <FiltersPanel
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            viewType={viewType}
            users={users}
            trainings={trainings}
            userQualifications={userQualifications}
            userEvents={userEvents}
            onFiltersChange={handleFiltersChange}
          />
        </div>
        <AddOrEditUserForm
          isCreatorOpen={isUserFormOpen}
          setIsCreatorOpen={setIsUserFormOpen}
          editingUserId={editingUserId}
          onSave={handleUserSave}
        />
        
        {isFileQualificationModalOpen && selectedUserForFiles && (
          <EditQualificationDialog
            isOpen={isFileQualificationModalOpen}
            onClose={handleCloseFileQualificationModal}
            qualificationData={null}
            fetchData={fetchData}
            userId={selectedUserForFiles.id}
            droppedFiles={draggedFiles}
            isMultiMode={draggedFiles.length > 1}
          />
        )}
      </Tooltip.Provider>
    </div>
  );
};

export default MainboardPanel;